import algoliasearch from 'algoliasearch/lite'
import { Checkbox, FormControl, ListItemText, MenuItem, Select } from '@material-ui/core'
import React, { useEffect, useMemo, useState } from 'react'
import styles from './AlgoliaCategoryFacet.module.scss'
import {
  generatePriceRangeOptions,
  resolveConfiguredFacets,
  resolveSiteIndexMap,
  resolveSortConfigForSite,
  sanitiseValuesForFacet,
} from '../utils/common'
import { FacetFieldValue, FacetOption, SiteCode, ValueOption } from '../common/types'

const EMPTY_MODEL: FacetFieldValue = {
  site: '',
  facet: '',
  values: [],
}

const AlgoliaCategoryFacet = ({ amplienceSdk }: any) => {
  const [model, setModel] = useState<FacetFieldValue>(EMPTY_MODEL)
  const [facetOptions, setFacetOptions] = useState<FacetOption[]>([])
  const [valueOptions, setValueOptions] = useState<ValueOption[]>([])
  const [loadingFacets, setLoadingFacets] = useState(false)
  const [loadingValues, setLoadingValues] = useState(false)
  const [error, setError] = useState<string>('')
  const [initialised, setInitialised] = useState(false)
  const [facetSelectOpen, setFacetSelectOpen] = useState(false)
  const [valuesSelectOpen, setValuesSelectOpen] = useState(false)

  const sdkParams = {
    ...amplienceSdk?.params?.installation,
    ...amplienceSdk?.params?.instance,
  }

  const appId = sdkParams.appId || process.env.REACT_APP_AQS_APP_ID
  const apiKey = sdkParams.apiKey || process.env.REACT_APP_AQS_APP_KEY
  const siteIndexMap = resolveSiteIndexMap(sdkParams)
  const configuredFacets = resolveConfiguredFacets(sdkParams)

  const searchClient = useMemo(() => {
    if (!appId || !apiKey) {
      return null
    }
    return algoliasearch(appId, apiKey)
  }, [appId, apiKey])

  const saveModel = async (value: FacetFieldValue) => {
    try {
      await amplienceSdk.field.setValue(value)
      setError('')
    } catch (saveError) {
      setError('Unable to save field value. Please review the current selection.')
      console.error('Amplience SDK: setValue failed', saveError)
    }
  }

  const getIndexNameForSite = (site: SiteCode | '') => {
    if (!site) {
      return ''
    }
    return siteIndexMap[site]
  }

  const selectedFacetOption = facetOptions.find((facet) => facet.attribute === model.facet)
  const isSingleValueFacet = selectedFacetOption?.component === 'sort' || selectedFacetOption?.component === 'price'
  const facetExistsInOptions = facetOptions.some((facet) => facet.attribute === model.facet)

  const loadFacetOptions = async (site: SiteCode) => {
    if (!searchClient) {
      setFacetOptions([])
      return [] as FacetOption[]
    }

    setLoadingFacets(true)
    try {
      const index = searchClient.initIndex(getIndexNameForSite(site))
      const response = await index.search('', {
        facets: ['*'],
        hitsPerPage: 0,
        maxValuesPerFacet: 1,
      })

      const availableFacetAttributes = Object.keys(response.facets || {})
      const matchedFacets = configuredFacets.filter(
        (facet) =>
          facet.component === 'sort' ||
          facet.component === 'price' ||
          availableFacetAttributes.includes(facet.attribute)
      )
      setFacetOptions(matchedFacets)
      setError('')
      return matchedFacets
    } catch (facetError) {
      setFacetOptions([])
      setError('Unable to load facets from Algolia for the selected site.')
      console.error('Algolia: failed to load facets', facetError)
      return [] as FacetOption[]
    } finally {
      setLoadingFacets(false)
    }
  }

  const loadFacetValues = async (site: SiteCode, facetName: string, facetOptionsOverride?: FacetOption[]) => {
    if (!facetName) {
      setValueOptions([])
      return []
    }
    const currency = site === 'IE' ? 'EUR' : 'GBP'

    const selectedFacet = (facetOptionsOverride ?? facetOptions).find((facet) => facet.attribute === facetName)
    const sortConfig = resolveSortConfigForSite(sdkParams, site)

    if (selectedFacet?.component === 'sort') {
      const sortValues = (sortConfig.items || []).map((item) => ({
        value: item.value,
        label: item.label,
      }))
      setValueOptions(sortValues)
      setError('')
      return sortValues
    }

    if (selectedFacet?.component === 'price') {
      const priceValues = generatePriceRangeOptions(currency)
      setValueOptions(priceValues)
      setError('')
      return priceValues
    }

    if (!searchClient) {
      setValueOptions([])
      return []
    }

    setLoadingValues(true)
    try {
      const index = searchClient.initIndex(getIndexNameForSite(site))
      const searchResponse = await index.search('', {
        facets: [facetName],
        maxValuesPerFacet: 1000,
        hitsPerPage: 0,
      })

      const facetData = searchResponse.facets?.[facetName] || {}
      const searchableFacetValues = Object.entries(facetData)
        .map(([value, count]) => {
          if (count > 0) {
            return { value, label: value }
          }
          return null
        })
        .filter((item): item is ValueOption => item !== null)
        .sort((a: ValueOption, b: ValueOption) => a.label.localeCompare(b.label))

      if (searchableFacetValues.length) {
        setValueOptions(searchableFacetValues)
        setError('')
        return searchableFacetValues
      }

      const fallbackResponse = await index.search('', {
        facets: [facetName],
        hitsPerPage: 0,
        maxValuesPerFacet: 100,
      })

      const facetValues = fallbackResponse?.facets?.[facetName] || {}
      const values = Object.keys(facetValues)
        .map((value) => ({ value, label: value }))
        .sort((a: ValueOption, b: ValueOption) => a.label.localeCompare(b.label))
      setValueOptions(values)
      setError('')
      return values
    } catch (valuesError) {
      setValueOptions([])
      setError('Unable to load facet values from Algolia for the selected facet.')
      console.error('Algolia: failed to load facet values', valuesError)
      return []
    } finally {
      setLoadingValues(false)
    }
  }

  useEffect(() => {
    amplienceSdk.frame.startAutoResizer()
  }, [])

  useEffect(() => {
    ;(async () => {
      const fieldValue = (await amplienceSdk.field.getValue()) || {}

      const hydratedModel: FacetFieldValue = {
        ...EMPTY_MODEL,
        ...fieldValue,
        values: Array.isArray(fieldValue.values) ? fieldValue.values : [],
      }

      setModel(hydratedModel)

      let availableFacetOptions: FacetOption[] = []
      if (hydratedModel.site) {
        availableFacetOptions = await loadFacetOptions(hydratedModel.site)
      }

      if (!hydratedModel.site || !hydratedModel.facet || !hydratedModel.values?.length) {
        setInitialised(true)
        return
      }

      const availableFacets = availableFacetOptions.map((facet) => facet.attribute)
      if (!availableFacets.includes(hydratedModel.facet)) {
        setModel((current) => ({
          ...current,
          facet: '',
          values: [],
        }))
        setInitialised(true)
        return
      }

      const availableValues = await loadFacetValues(hydratedModel.site, hydratedModel.facet, availableFacetOptions)
      const sanitisedValues = sanitiseValuesForFacet({
        facetName: hydratedModel.facet,
        currentValues: hydratedModel.values,
        availableValues,
        configuredFacets,
      })

      if (sanitisedValues.length !== hydratedModel.values.length) {
        setModel((current) => ({ ...current, values: sanitisedValues }))
      }

      setInitialised(true)
    })()
  }, [])

  useEffect(() => {
    if (!initialised) {
      return
    }
    saveModel(model)
  }, [model, initialised])

  const onSiteChange = async (value: SiteCode | '') => {
    setError('')
    setModel((current) => ({
      ...current,
      site: value,
      facet: '',
      values: [],
    }))
    setValueOptions([])

    if (value) {
      await loadFacetOptions(value)
    } else {
      setFacetOptions([])
    }
  }

  const onFacetChange = async (facetName: string) => {
    setError('')
    setModel((current) => ({
      ...current,
      facet: facetName,
      values: [],
    }))

    if (model.site && facetName) {
      await loadFacetValues(model.site, facetName)
    } else {
      setValueOptions([])
    }
  }

  const onValuesChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value
    const nextValues = Array.isArray(value) ? value : [value].filter(Boolean)
    const sanitisedValues = isSingleValueFacet ? nextValues.slice(-1) : nextValues
    setModel((current) => ({
      ...current,
      values: sanitisedValues,
    }))
  }

  return (
    <div className={styles.container}>
      <div className={styles.field}>
        <fieldset className={styles.siteFieldset}>
          <legend className={styles.fieldLabel}>Site Selection</legend>
          <div className={styles.siteSelector}>
            <button
              type="button"
              className={`${styles.siteButton} ${model.site === 'UK' ? styles.siteButtonActive : ''}`}
              onClick={() => onSiteChange('UK')}
            >
              Interflora
            </button>
            <button
              type="button"
              className={`${styles.siteButton} ${model.site === 'IE' ? styles.siteButtonActive : ''}`}
              onClick={() => onSiteChange('IE')}
            >
              Ireland
            </button>
          </div>
        </fieldset>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="facetSelection">
          Facet
        </label>
        <p className={styles.hint}>Select facet to be displayed</p>
        {facetSelectOpen && (
          <button
            type="button"
            className={styles.dropdownCloseButton}
            onMouseDown={(e) => {
              e.preventDefault()
              setFacetSelectOpen(false)
            }}
          >
            Close ✕
          </button>
        )}
        <FormControl variant="outlined" className={styles.formControl}>
          <Select
            id="facetSelection"
            className={styles.select}
            open={facetSelectOpen}
            onOpen={() => setFacetSelectOpen(true)}
            onClose={() => setFacetSelectOpen(false)}
            value={facetExistsInOptions ? model.facet : ''}
            onChange={(event) => onFacetChange(event.target.value as string)}
            disabled={!model.site || loadingFacets}
            displayEmpty
            MenuProps={{
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              getContentAnchorEl: null,
            }}
          >
            <MenuItem value="">Select an option</MenuItem>
            {facetOptions.map((facet) => (
              <MenuItem key={facet.attribute} value={facet.attribute}>
                {facet.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {loadingFacets && <div className={styles.loading}>Loading facets...</div>}
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="valuesSelection">
          Values
        </label>
        <p className={styles.hint}>Multi-select values for the selected facet only.</p>
        <FormControl variant="outlined" className={styles.formControl}>
          <Select
            id="valuesSelection"
            className={styles.select}
            multiple
            open={valuesSelectOpen}
            onOpen={() => setValuesSelectOpen(true)}
            onClose={() => setValuesSelectOpen(false)}
            value={model.values}
            onChange={onValuesChange}
            disabled={!model.site || !model.facet || loadingValues}
            displayEmpty
            MenuProps={{
              anchorOrigin: { vertical: 'top', horizontal: 'left' },
              transformOrigin: { vertical: 'bottom', horizontal: 'left' },
              getContentAnchorEl: null,
            }}
            renderValue={(selected) => {
              const selectedValues = Array.isArray(selected)
                ? (selected as string[])
                : [selected as string].filter(Boolean)
              if (!selectedValues.length) {
                return 'Select an option'
              }
              const selectedLabels = valueOptions
                .filter((option) => selectedValues.includes(option.value))
                .map((option) => option.label)
              return selectedLabels.join(', ')
            }}
          >
            {valueOptions.map((value) => (
              <MenuItem key={value.value} value={value.value}>
                <Checkbox checked={model.values.includes(value.value)} color="primary" />
                <ListItemText primary={value.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {valuesSelectOpen && (
          <button
            type="button"
            className={styles.dropdownCloseButton}
            onMouseDown={(e) => {
              e.preventDefault()
              setValuesSelectOpen(false)
            }}
          >
            Close ✕
          </button>
        )}
        {loadingValues && <div className={styles.loading}>Loading values...</div>}
      </div>

      {error && <div className={styles.error}>{error}</div>}
    </div>
  )
}

export default AlgoliaCategoryFacet
